import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    hardLimit: { type: Number, default: 4000 },
    maxAnnualHours: Number,
  };

  connect() {
    this.wrapper = this.element;
    this.input = this.element.querySelector('input[type="number"]');

    this.entryForm = this.wrapper.closest('[data-payroll-entry]');
    this._findContainers();

    this.debouncedValidate = this.#debounce(() => this._doValidate(), 200);
    this.wrapper.addEventListener("totalHoursValidation:serverError", this._handleServerError.bind(this));
    this._doValidate();
  }

  _findContainers() {
    const wrapperParent = this.wrapper?.parentElement;

    if (wrapperParent) {
      this.errorContainer = wrapperParent.querySelector('[data-total-hours-validation-target="errorContainer"]');
      this.warningContainer = wrapperParent.querySelector('[data-total-hours-validation-target="warningContainer"]');
    }
  }

  disconnect() {
    if (this.wrapper) {
      this.wrapper.removeEventListener("totalHoursValidation:serverError", this._handleServerError);
    }
  }

  validate(event) {
    if (event?.target?.tagName === 'INPUT') {
      this.input = event.target;
    }

    if (event?.type === 'input') {
      this.debouncedValidate();
    } else {
      this._doValidate();
    }
  }

  _doValidate() {
    if (!this.input) {
      this.input = this.element.querySelector('input[type="number"]');
    }
    if (!this.input) return;

    const value = parseFloat(this.input.value) || 0;

    if (value > this.hardLimitValue) {
      this._showError(`Ordinary hours worked cannot exceed ${this.hardLimitValue} hours per year`);
      this._markIncomplete();
      this._hideWarning();
      return;
    }

    this._hideError();

    if (this.hasMaxAnnualHoursValue && this.maxAnnualHoursValue != null && Number(this.maxAnnualHoursValue) > 0) {
      const maxAnnualHours = Number(this.maxAnnualHoursValue);
      if (value > 0 && value > maxAnnualHours) {
        this._showWarning("Hours exceed the maximum annual hours limit for this country");
      } else {
        this._hideWarning();
      }
    } else {
      this._hideWarning();
    }
  }

  _showError(message) {
    if (!this.errorContainer) {
      this._findContainers();
    }

    if (this.errorContainer) {
      const span = this.errorContainer.querySelector("span");
      if (span) {
        span.textContent = message;
      }
      this.errorContainer.classList.remove("hidden");
    }

    if (this.input) {
      this.input.classList.add("border-red-500");
    }
  }

  _hideError() {
    if (this.errorContainer) {
      this.errorContainer.classList.add("hidden");
    }
    if (this.input) {
      this.input.classList.remove("border-red-500");
    }
  }

  _showWarning(message) {
    if (!this.warningContainer) {
      this._findContainers();
    }
    
    if (this.warningContainer) {
      const span = this.warningContainer.querySelector("span");
      if (span) {
        span.textContent = message;
      }
      this.warningContainer.classList.remove("hidden");
    }
  }

  _hideWarning() {
    if (this.warningContainer) {
      this.warningContainer.classList.add("hidden");
    }
  }

  _markIncomplete() {
    if (this.entryForm) {
      this.entryForm.setAttribute("data-payroll-entry-incomplete", "true");
    }
  }

  _handleServerError(event) {
    const { field, message } = event.detail;
    if (!this.input) return;
    
    const fieldName = this.input.name || "";
    if (fieldName.includes(field.split('.').pop()) || fieldName.includes(field)) {
      this._showError(message);
      this._markIncomplete();
    }
  }

  #debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  }
}
